import React, {useEffect, useRef, useState} from 'react';
import clsx from 'clsx';

import {faEllipsisH} from '@netx/core-icons';
import {
	Popover,
	type PopoverProps,
	type PopoverActions,
	popoverClasses,
} from '@mui/material';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import IconButton from '@netx/core-components/IconButton';
import {breadcrumbClasses} from '@netx/core-components/Breadcrumb/breadcrumbClasses';
import {
	breadcrumbTruncatedMenuClasses,
	getBreadcrumbTruncatedMenuUtilityClass,
	type BreadcrumbTruncatedMenuClasses,
	BREADCRUMB_TRUNCATED_MENU_CLASS_NAME,
} from './breadcrumbsTruncatedMenuClasses';
import {BreadcrumbLabels} from './locales';

type UtilityClassProps = WithClasses<BreadcrumbTruncatedMenuClasses, {}>;

const useUtilityClasses = (ownerState: UtilityClassProps) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
		popover: ['popover'],
	};
	return composeClasses(slots, getBreadcrumbTruncatedMenuUtilityClass, classes);
};

const BreadcrumbTruncatedMenuRoot = styled('li', {
	name: BREADCRUMB_TRUNCATED_MENU_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	flex: '0 0 auto',
}));

const BreadcrumbTruncatedMenuPopover = styled(Popover, {
	name: BREADCRUMB_TRUNCATED_MENU_CLASS_NAME,
	slot: 'Popover',
})(({theme}) => ({
	[`& .${popoverClasses.paper}`]: {
		padding: 0,
		display: 'flex',
		flexDirection: 'column',
		[`& .${breadcrumbClasses.root}`]: {
			'& > p, & > a': {
				minWidth: '100%',
				padding: theme.spacing(1, 2),
			},
			'&:hover > a': {
				background: theme.palette.action.hover,
			},
		},
	},
}));

export type BreadcrumbTruncatedMenuProps = WithClasses<
	BreadcrumbTruncatedMenuClasses,
	{
		children: React.ReactNode[];
		label?: string;
		PopoverProps?: Partial<PopoverProps>;
	}
>;

export const BreadcrumbTruncatedMenuPrimitive = ({
	children,
	classes: _classes,
	className,
	label,
	PopoverProps,
}: BreadcrumbTruncatedMenuProps) => {
	const [anchorEl, setAnchorEl] = useState<HTMLElement>();
	const open = Boolean(anchorEl);

	const ownerState: UtilityClassProps = {
		classes: _classes,
		className,
	};

	const classes = useUtilityClasses(ownerState);

	useEffect(() => {
		if (children.length) return;
		setAnchorEl(undefined);
	}, [children]);

	const popoverActions = useRef<PopoverActions>(null);

	const handleClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) =>
		setAnchorEl(event.currentTarget);

	const handleClose = () => setAnchorEl(undefined);

	return (
		<BreadcrumbTruncatedMenuRoot
			className={clsx(classes.root, className, 'hidden-menu')}
		>
			<IconButton
				id="breadcrumb-overflow-menu-button"
				label={label || BreadcrumbLabels.SHOW_MORE}
				size="small"
				icon={faEllipsisH}
				onClick={handleClick}
			/>

			<BreadcrumbTruncatedMenuPopover
				anchorEl={anchorEl}
				className={classes.popover}
				open={open}
				onClose={handleClose}
				onClick={handleClose}
				action={popoverActions}
				id={`breadcrumb-overflow-menu`}
				aria-labelledby={`breadcrumb-overflow-menu-button`}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'left',
				}}
				{...PopoverProps}
			>
				{children}
			</BreadcrumbTruncatedMenuPopover>
		</BreadcrumbTruncatedMenuRoot>
	);
};

export const BreadcrumbTruncatedMenu = BreadcrumbTruncatedMenuPrimitive;

export default BreadcrumbTruncatedMenu;
